import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GripVertical, LayoutGrid } from 'lucide-react';
import {
  DEFAULT_EXECUTIVE_SCORECARD_ORDER,
  DEFAULT_EXECUTIVE_TILE_CATALOG,
  DEFAULT_EXECUTIVE_TOP_KPI_ORDER,
} from '../../config/executiveTileCatalog';
import { customExecutiveMetricsService } from '../../services/customExecutiveMetricsService';

function reorderList(list, fromId, toId) {
  if (!fromId || !toId || fromId === toId) return list;
  const next = [...list];
  const fromIdx = next.indexOf(fromId);
  const toIdx = next.indexOf(toId);
  if (fromIdx < 0 || toIdx < 0) return list;
  next.splice(fromIdx, 1);
  next.splice(toIdx, 0, fromId);
  return next;
}

function SortableList({ title, itemIds, catalog, idKey, labelKey, disabledTop, onToggle, onReorder, disabled, saving }) {
  const catalogById = useMemo(
    () => new Map(catalog.map((item) => [item[idKey], item])),
    [catalog, idKey]
  );

  const [dragId, setDragId] = useState(null);

  const handleDrop = (targetId) => {
    if (!dragId) return;
    onReorder(reorderList(itemIds, dragId, targetId));
    setDragId(null);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <p className="text-sm font-medium text-gray-800 mb-1">{title}</p>
      <p className="text-xs text-gray-500 mb-3">Drag to reorder. Uncheck to hide from the dashboard.</p>
      <ul className="space-y-2">
        {itemIds.map((id) => {
          const item = catalogById.get(id);
          if (!item) return null;
          const label = item[labelKey];
          const hidden = disabledTop.has(id);
          return (
            <li
              key={id}
              draggable={!disabled && !saving}
              onDragStart={() => setDragId(id)}
              onDragEnd={() => setDragId(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(id)}
              className={`flex items-center gap-2 text-sm rounded-md px-2 py-1.5 border ${
                dragId === id ? 'border-blue-400 bg-blue-50' : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
              }`}
            >
              <GripVertical className="w-4 h-4 text-gray-400 shrink-0 cursor-grab" />
              <input
                type="checkbox"
                checked={!hidden}
                onChange={() => onToggle(id)}
                disabled={disabled || saving}
              />
              <span className={hidden ? 'text-gray-400 line-through' : 'text-gray-800'}>{label}</span>
              {item.provider && (
                <span className="text-xs text-gray-400 ml-auto">{item.provider}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function ExecutiveTileLayoutSection({ organizationId, disabled }) {
  const defaultTopOrder = DEFAULT_EXECUTIVE_TILE_CATALOG.topKpis.map((t) => t.id);
  const defaultScorecardOrder = DEFAULT_EXECUTIVE_TILE_CATALOG.scorecards.map((s) => s.id);

  const [layout, setLayout] = useState({
    disabledTopKpiIds: [],
    disabledScorecardIds: [],
    topKpiOrder: defaultTopOrder,
    scorecardOrder: defaultScorecardOrder,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadLayout = useCallback(async () => {
    if (!organizationId) return;
    const data = await customExecutiveMetricsService.getLayoutOverrides(organizationId, {
      includeHistory: false,
    });
    const loaded = data.layout || {};
    setLayout({
      disabledTopKpiIds: loaded.disabledTopKpiIds || [],
      disabledScorecardIds: loaded.disabledScorecardIds || [],
      topKpiOrder: loaded.topKpiOrder?.length ? loaded.topKpiOrder : DEFAULT_EXECUTIVE_TOP_KPI_ORDER,
      scorecardOrder: loaded.scorecardOrder?.length
        ? loaded.scorecardOrder
        : DEFAULT_EXECUTIVE_SCORECARD_ORDER,
    });
  }, [organizationId]);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  const toggleTopKpi = (id) => {
    setLayout((prev) => {
      const set = new Set(prev.disabledTopKpiIds || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, disabledTopKpiIds: [...set] };
    });
  };

  const toggleScorecard = (id) => {
    setLayout((prev) => {
      const set = new Set(prev.disabledScorecardIds || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, disabledScorecardIds: [...set] };
    });
  };

  const handleSave = async () => {
    if (!organizationId) return;
    setSaving(true);
    setMessage('');
    try {
      const existing = await customExecutiveMetricsService.getLayoutOverrides(organizationId);
      await customExecutiveMetricsService.saveLayoutOverrides(
        organizationId,
        { ...(existing.layout || {}), ...layout },
        null
      );
      setMessage('Tile layout saved for your organization.');
    } catch (e) {
      setMessage(e.message || 'Failed to save layout.');
    } finally {
      setSaving(false);
    }
  };

  const disabledTop = new Set(layout.disabledTopKpiIds || []);
  const disabledScore = new Set(layout.disabledScorecardIds || []);

  const topOrder = useMemo(() => {
    const known = new Set(DEFAULT_EXECUTIVE_TOP_KPI_ORDER);
    const ordered = (layout.topKpiOrder || []).filter((id) => known.has(id));
    DEFAULT_EXECUTIVE_TOP_KPI_ORDER.forEach((id) => {
      if (!ordered.includes(id)) ordered.push(id);
    });
    return ordered;
  }, [layout.topKpiOrder]);

  const scorecardOrder = useMemo(() => {
    const known = new Set(DEFAULT_EXECUTIVE_SCORECARD_ORDER);
    const ordered = (layout.scorecardOrder || []).filter((id) => known.has(id));
    DEFAULT_EXECUTIVE_SCORECARD_ORDER.forEach((id) => {
      if (!ordered.includes(id)) ordered.push(id);
    });
    return ordered;
  }, [layout.scorecardOrder]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <LayoutGrid className="w-5 h-5 text-blue-600" />
          Executive tile layout
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Show, hide, and reorder catalog tiles on the Executive KPI Dashboard. Custom metrics you add
          separately are always shown.
        </p>
      </div>

      {disabled && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Select an organization to manage tile layout.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SortableList
          title="Top KPI row"
          itemIds={topOrder}
          catalog={DEFAULT_EXECUTIVE_TILE_CATALOG.topKpis}
          idKey="id"
          labelKey="label"
          disabledTop={disabledTop}
          onToggle={toggleTopKpi}
          onReorder={(next) => setLayout((prev) => ({ ...prev, topKpiOrder: next }))}
          disabled={disabled}
          saving={saving}
        />
        <SortableList
          title="Department scorecards"
          itemIds={scorecardOrder}
          catalog={DEFAULT_EXECUTIVE_TILE_CATALOG.scorecards}
          idKey="id"
          labelKey="title"
          disabledTop={disabledScore}
          onToggle={toggleScorecard}
          onReorder={(next) => setLayout((prev) => ({ ...prev, scorecardOrder: next }))}
          disabled={disabled}
          saving={saving}
        />
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={disabled || saving}
        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save tile layout'}
      </button>
      {message && (
        <p className={`text-sm ${message.includes('Failed') ? 'text-red-600' : 'text-green-700'}`}>{message}</p>
      )}
    </div>
  );
}
